import type { TFetchError } from '@/api/api-error.types';

/**
 * The chat 4xx codes the UI branches on, as the wire values from `ChatErrorCodes`
 * (`libs/common/src/enums/chat-error-codes.enum.ts`). Chat surfaces decide their copy from the
 * code, never from the message (D29).
 */
export const CHAT_ERROR_CODES = {
  threadNotFound: 'CHAT.THREAD.NOT_FOUND',
  threadCreateForbidden: 'CHAT.THREAD.CREATE_FORBIDDEN',
  threadViewForbidden: 'CHAT.THREAD.VIEW_FORBIDDEN',
  threadResolveForbidden: 'CHAT.THREAD.RESOLVE_FORBIDDEN',
  threadRenameInvalid: 'CHAT.THREAD.RENAME_INVALID',
  threadReplyTargetInvalid: 'CHAT.THREAD.REPLY_TARGET_INVALID',
  threadTopicRequired: 'CHAT.THREAD.TOPIC_REQUIRED',
  privateRequiresSharedInbox: 'CHAT.PRIVATE.REQUIRES_SHARED_INBOX',

  messageNotFound: 'CHAT.MESSAGE.NOT_FOUND',
  messageSendForbidden: 'CHAT.MESSAGE.SEND_FORBIDDEN',
  messageAccessForbidden: 'CHAT.MESSAGE.ACCESS_FORBIDDEN',
  messageContentRequired: 'CHAT.MESSAGE.CONTENT_REQUIRED',
  messageReferenceExclusive: 'CHAT.MESSAGE.REFERENCE_EXCLUSIVE',
  replyToDifferentThread: 'CHAT.MESSAGE.REPLY_TO_DIFFERENT_THREAD',
  forwardSourceForbidden: 'CHAT.MESSAGE.FORWARD_SOURCE_FORBIDDEN',

  /** 410 — the named `data.context.fileIds` vanished; re-request the upload and send once more. */
  attachmentUnavailable: 'CHAT.ATTACHMENT.UNAVAILABLE',

  participantNotFound: 'CHAT.PARTICIPANT.NOT_FOUND',
  participantNotInThread: 'CHAT.PARTICIPANT.NOT_IN_THREAD',
  participantManageForbidden: 'CHAT.PARTICIPANT.MANAGE_FORBIDDEN',
  participantRoleForbidden: 'CHAT.PARTICIPANT.ROLE_FORBIDDEN',

  viewForbidden: 'CHAT.ACCESS.VIEW_FORBIDDEN',
  /** Writing into a thread held by another of the caller's identities. */
  accessUnauthorized: 'CHAT.ACCESS.UNAUTHORIZED',
  identityReadOnly: 'CHAT.IDENTITY.READ_ONLY',

  contextNotFound: 'CHAT.CONTEXT.NOT_FOUND',
  contextForbidden: 'CHAT.CONTEXT.FORBIDDEN',
  feedCursorInvalid: 'CHAT.FEED.CURSOR_INVALID',

  topicNotFound: 'CHAT.TOPIC.NOT_FOUND',
  topicNameConflict: 'CHAT.TOPIC.NAME_CONFLICT',
  topicSystemImmutable: 'CHAT.TOPIC.SYSTEM_IMMUTABLE',
  topicNotOwned: 'CHAT.TOPIC.NOT_OWNED',
  topicLabelInvalid: 'CHAT.TOPIC.LABEL_INVALID',
  topicVisibilityForbidden: 'CHAT.TOPIC.VISIBILITY_FORBIDDEN',
  topicLocked: 'CHAT.TOPIC.LOCKED',
  topicNameReserved: 'CHAT.TOPIC.NAME_RESERVED',

  filingInvalidTarget: 'CHAT.FILING.INVALID_TARGET',

  broadcastWriteRestriction: 'CHAT.BROADCAST.WRITE_RESTRICTION',

  recipientInvalid: 'CHAT.RECIPIENT.INVALID',
  recipientNotPermitted: 'CHAT.RECIPIENT.NOT_PERMITTED',
  recipientInvalidDesk: 'CHAT.RECIPIENT.INVALID_DESK',
} as const;

export type ChatErrorCode = (typeof CHAT_ERROR_CODES)[keyof typeof CHAT_ERROR_CODES];

const CHAT_ERROR_CODE_VALUES = new Set<string>(Object.values(CHAT_ERROR_CODES));

export const isChatErrorCode = (code: unknown): code is ChatErrorCode =>
  typeof code === 'string' && CHAT_ERROR_CODE_VALUES.has(code);

export const getChatErrorCode = (error: unknown): ChatErrorCode | undefined => {
  const code = (error as TFetchError | undefined)?.data?.code;

  return isChatErrorCode(code) ? code : undefined;
};

export const isChatErrorOf = (error: unknown, ...codes: ChatErrorCode[]): boolean => {
  const code = getChatErrorCode(error);

  return code !== undefined && codes.includes(code);
};
